// Authentication and Authorization Types

/** Portal-supported cultures; used when Permissions API omits `allowedCulture`. */
export const DEFAULT_ALLOWED_CULTURES = ['en-US', 'ar-EG', 'en', 'ar'] as const;

export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  roles: UserRole[];
  countries: string[];
  features: string[];
  /** Locales permitted for UI (from Permissions API `allowedCulture`). */
  allowedCulture?: string[];
  lastLoginAt?: Date;
  isActive: boolean;
  // Raw backend permissions - no transformation
  pages: any[]; // Direct from backend API data.pages (BackendPage | BackendPageNew | BackendPageUpdated)
}

export interface UserRole {
  id: string;
  name: string;
  description: string;
  permissions: Permission[];
  countries: string[];
  reports: string[];
  isSystemRole: boolean;
}

export interface Permission {
  id: string;
  resource: string;
  actions: ('read' | 'write' | 'delete' | 'admin')[];
  conditions?: Record<string, any>;
  description: string;
}

// Backend permission structure
export interface BackendPermission {
  id: number;
  name: string;
  description: string | null;
  identifier: string;
  type: number;
}

export interface BackendPage {
  labels: string;
  appName: string;
  actions: BackendPermission[];
  description: string | null;
  id: number;
  identifier: string;
  name: string;
  type: number;
}

/**
 * New backend page format with extended fields for dynamic menu generation
 */
export interface BackendPageNew {
  id: number;
  identifier: string; // e.g., "page:payout"
  name: string; // Category name, e.g., "Payment"
  appName: string; // e.g., "payout"
  labels: string; // Legacy label
  description: string | null;
  type: number;

  // New fields for dynamic menu
  Order: number;
  DisplayName: string; // e.g., "Payout Report"
  DisplayIcon: string; // e.g., "dollar"
  ReportPath: string; // e.g., "/payment/payout-report/"
  ParentDisplayName: string; // e.g., "Payments Reports"
  ParentDisplayIcon: string; // e.g., "dollarCircle"
  ParentReportPath: string; // e.g., "/payment"

  actions: BackendPermission[];
}

/**
 * Updated backend page format with corrected field names
 */
export interface BackendPageUpdated {
  id: number;
  identifier: string; // e.g., "page:payout"
  name: string; // Category name, e.g., "Payment"
  appName: string; // e.g., "payout"
  labels: string; // Legacy label
  description: string | null;
  type: number;

  // Updated fields for dynamic menu (corrected field names)
  order: number;
  diplayName: string; // Note: typo in backend field name
  displayIcon: string; // e.g., "dollar"
  reportPath: string; // e.g., "/payment/payout-report/"
  parentDisplayName: string; // e.g., "Payments Reports"
  parentDisplayIcon: string; // e.g., "dollar"
  parentReportPath: string; // e.g., "/payment"

  actions: BackendPermission[];
}

/**
 * Page-level action permissions
 */
export interface PageActionPermissions {
  pageId: string; // identifier (e.g., "page:payout")
  pagePath: string; // ReportPath
  actions: BackendPermission[]; // Available actions for this page
  type: number; // 1=page/report, 2=permission
}

/**
 * RECOMMENDED: Backend should return menu items in ProLayout format
 * This eliminates all frontend transformation and hardcoding
 */
export interface BackendPermissionsResponse {
  data: {
    menuItems?: NavigationItem[]; // Ready-to-use ProLayout menu structure
    permissions?: string[]; // Flat list of all permission identifiers
    pages?: BackendPage[] | BackendPageNew[]; // Legacy or new format (optional, for backward compatibility)
    countries?: string[]; // Optional: List of country codes user has access to
    features?: string[]; // Optional: List of feature flags enabled for user
    allowedCulture?: string[]; // Optional: BCP 47 locales allowed for UI (e.g. en-US, ar-EG)
  };
  count: number;
  responseCode: number;
  statusCode: number;
  responseMessage: string;
  errors: any;
}

export interface ReportPermission {
  reportId: string;
  reportName: string;
  actions: BackendPermission[];
  canAccess: boolean;
}

export interface AuthProvider {
  id: string;
  name: string;
  type: 'azure-ad' | 'local' | 'saml' | 'oauth';
  isEnabled: boolean;
  config: Record<string, any>;
}

export interface LoginRequest {
  provider: string;
  email?: string;
  password?: string;
  code?: string; // For OAuth flows
  state?: string; // For OAuth flows
}

export interface LoginResponse {
  success: boolean;
  user?: User;
  token?: string;
  refreshToken?: string;
  expiresIn?: number;
  error?: string;
}

export interface TokenInfo {
  accessToken: string;
  refreshToken: string;
  expiresAt: Date;
  tokenType: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  providers: AuthProvider[];
}

// ProLayout-compatible menu item (what ProLayout expects)
export interface ProLayoutMenuItem {
  path: string;
  name?: string; // i18n key only
  icon?: string | React.ReactNode;
  key?: string; // React key for unique identification
  routes?: ProLayoutMenuItem[];
  hideInMenu?: boolean;
  hideChildrenInMenu?: boolean;
  redirect?: string;
}

// Internal navigation item (for processing and permission checks)
export interface InternalNavigationItem extends ProLayoutMenuItem {
  identifier?: string; // page:payout (for permission checks)
  permissions?: string[]; // action identifiers
  order?: number; // for sorting
  displayName?: string; // for i18n files
}

// Keep NavigationItem as alias for backward compatibility
export type NavigationItem = ProLayoutMenuItem;

export interface AuditLog {
  id: string;
  userId: string;
  action: string;
  resource: string;
  timestamp: Date;
  ipAddress: string;
  userAgent: string;
  details: Record<string, any>;
  country: string;
  success: boolean;
}
